import React, { lazy } from 'react'
import { Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom'

import FullscreenLoader from 'src/components/base/fullscreenLoader/FullscreenLoader'
import NotFound from 'src/components/base/notFound/NotFound'
import RequireAuth from 'src/components/presentationals/auth/RequireAuth'
import RequireUnAuth from 'src/components/presentationals/auth/RequireUnAuth'
import ErrorBoundary from 'src/components/presentationals/shared/errors/ErrorBoundary'
import { useUser } from 'src/contexts/userContext'
import { useSyncCurrentMembership } from 'src/hooks/useSyncCurrentMembership'
import { routerUserRedirects } from 'src/utils/router'

import { PasswordUpdateModal } from './components/presentationals/shared/forms/PasswordUpateModal/PasswordUpdateModal'
import { MembershipProvider } from './contexts/MembershipContext'
import { getUserType } from './contexts/userContext'
import { UserType } from './types'

const AsyncBuyerDashboard = lazy(
  () => import('./components/presentationals/buyer-dashboard/BuyerDashboard')
)

const AsyncSellerDashboard = lazy(
  () => import('./components/presentationals/seller-dashboard/SellerDashboard')
)

const AsyncNotifications = lazy(() => import('./components/containers/Notifications'))

const AsyncLoadingBar = lazy(() => import('./components/containers/LoadingBar'))

const AsyncLogin = lazy(() => import('./components/presentationals/auth/Login/Login'))

const AsyncBuyerSignup = lazy(() => import('./components/presentationals/auth/buyer-signup'))

const AsyncSellerSignup = lazy(() => import('./components/presentationals/auth/seller-signup'))

const AsyncSignupDone = lazy(() => import('./components/presentationals/auth/SignupDone'))

const AsyncForgotPassword = lazy(
  () => import('./components/presentationals/auth/forgot-password/ForgotPassword')
)

const AsyncForgotPasswordDone = lazy(
  () => import('./components/presentationals/auth/forgot-password/ForgotPasswordDone')
)

const AsyncResetPassword = lazy(
  () => import('./components/presentationals/auth/reset-password/ResetPassword')
)

const AsyncResetPasswordDone = lazy(
  () => import('./components/presentationals/auth/reset-password/ResetPasswordDone')
)

const AsyncMagicLinkConfirm = lazy(
  () => import('./components/presentationals/auth/MagicLinkConfirm/MagicLinkConfirm')
)

const AsyncDirectory = lazy(() => import('./directory/Directory'))

const AsyncGcpLandingPage = lazy(() => import('./landing-pages/GcpLandingPage/GcpLandingPage'))

const buyerPath = '/:locale/:userType(buyer)'
const buyerOrSellerPath = '/:locale/:userType(buyer|seller)'
const stopImpPath = '/:locale/imp/stop'
const loginPath = '/:locale/accounts/login'
export const magicLinkPath = '/:locale/accounts/magic-link'
const buyerSignupPath = '/:locale/accounts/buyer'
const sellerSignupPath = '/:locale/accounts/seller'
const signupDonePath = '/:locale/accounts/:userType(buyer|seller)/done'
const forgotPasswordPath = '/:locale/accounts/password_reset'
const forgotPasswordDonePath = '/:locale/accounts/password_reset/done'
const resetPasswordPath = '/:locale/accounts/reset'
const resetPasswordDonePath = '/:locale/accounts/reset/done'
const directoryPath = '/:locale/catalog'
const logoutPath = '/:locale/logout'
const logoutProfilePath = '/:locale/logout-profile'
const gcpLandingPage = '/:locale/register'

const Routes = () => {
  const { pathname, search } = useLocation()
  const user = useUser()
  const matchLocale = useRouteMatch({ path: '/:locale' })

  const { effectiveMembership } = useSyncCurrentMembership(user)

  const matchBuyerOrSellerPath = useRouteMatch({ path: buyerOrSellerPath })

  const userType = getUserType({
    membership: effectiveMembership,
    user,
  })

  return matchLocale ? (
    <>
      <Switch>
        <Route path={loginPath}>
          <RequireUnAuth>
            <AsyncLogin />
          </RequireUnAuth>
        </Route>
        <Route exact path={magicLinkPath}>
          <AsyncMagicLinkConfirm />
        </Route>
        <Route exact path={forgotPasswordPath}>
          <AsyncForgotPassword />
        </Route>
        <Route exact path={forgotPasswordDonePath}>
          <AsyncForgotPasswordDone />
        </Route>
        <Route exact path={resetPasswordPath}>
          <AsyncResetPassword />
        </Route>
        <Route exact path={resetPasswordDonePath}>
          <AsyncResetPasswordDone />
        </Route>
        <Route exact path={buyerSignupPath}>
          <AsyncBuyerSignup />
        </Route>
        <Route exact path={sellerSignupPath}>
          <AsyncSellerSignup />
        </Route>
        <Route exact path={signupDonePath}>
          <AsyncSignupDone />
        </Route>
        <Route path={directoryPath}>
          <AsyncDirectory />
        </Route>
        <Route exact path={gcpLandingPage}>
          <AsyncGcpLandingPage />
        </Route>

        {!user && (
          <Route
            path="/:locale"
            children={({ match }) =>
              match ? (
                <Redirect
                  to={{
                    pathname: `/${match.params.locale}/accounts/login`,
                    search: `?next=${encodeURIComponent(`${pathname}${search}`)}`,
                  }}
                />
              ) : null
            }
          />
        )}

        {!!user && userType === UserType.ADMIN && (
          <Route exact path="*">
            <RequireAuth>
              <FullscreenLoader text={gettext('Opening admin')} />
            </RequireAuth>
          </Route>
        )}
        {!!user && !!effectiveMembership && userType === UserType.BUYER && (
          <Route path={buyerPath}>
            <RequireAuth>
              <MembershipProvider membership={effectiveMembership}>
                <AsyncBuyerDashboard />
              </MembershipProvider>
            </RequireAuth>
          </Route>
        )}
        {!!user && !!effectiveMembership && userType === UserType.SELLER && (
          <Route path={buyerOrSellerPath}>
            <RequireAuth>
              <MembershipProvider membership={effectiveMembership}>
                <AsyncSellerDashboard />
              </MembershipProvider>
            </RequireAuth>
          </Route>
        )}

        <Route
          exact
          path="/:locale"
          children={({ match }) =>
            match ? (
              <Redirect
                to={{ pathname: `/${match.params.locale}${routerUserRedirects[userType]}`, search }}
              />
            ) : null
          }
        />

        <Route exact path={logoutPath}>
          <FullscreenLoader text={gettext('Logging out')} />
        </Route>

        <Route exact path={logoutProfilePath}>
          <FullscreenLoader text={gettext('Logging out of profile')} />
        </Route>

        <Route exact path={stopImpPath}>
          <RequireAuth>
            <FullscreenLoader text={gettext('Impersonation')} />
          </RequireAuth>
        </Route>

        <Route path="*">
          <ErrorBoundary>
            <NotFound />
          </ErrorBoundary>
        </Route>
      </Switch>

      {matchBuyerOrSellerPath && <AsyncLoadingBar />}

      <AsyncNotifications />

      {user && userType !== UserType.ADMIN && !user.impersonatorIsStaff && (
        <PasswordUpdateModal open={!user.userPasswordIsSet} />
      )}
    </>
  ) : null
}

export default Routes
